import { consultarSimple, consultarMultiple, consultarConRuta } from "./crud";
import { obtenerNits, CONFIG, log, mesesCompletos } from "./variables_globales";
import { get, ref, } from "firebase/database";
import { db } from "./api_firebase";
import { crearLog } from "../pages/components/logs/logs";
import { zonasActivasDelConjunto } from "./zonas_catalogo";

const enLotes = async (items, tarea, tamano = 100) => {
  const salida = [];
  for (let i = 0; i < items.length; i += tamano) {
    const lote = await Promise.all(items.slice(i, i + tamano).map(tarea));
    salida.push(...lote);
  }
  return salida;
};

export const obtenerInformes = async () => {
  const esSuper = CONFIG.esSuper();
  
  try {
    if (esSuper) {
      const informes = {};
      
      const nits = await obtenerNits();
      const promesas = nits.map(async (nit) => {
        try {
          const snapshot = await get(ref(db, `/informe/${nit}`));
          const datos = snapshot.val();
          
          if (datos) {
            informes[nit] = datos;
          }
          
          await crearLog(`/informe/${nit}`, "leer");
        } catch (error) {
          console.error(`Error obteniendo informe de NIT ${nit}:`, error.message);
        }
      });
      
      await Promise.all(promesas);
      
      log("✅ Informes obtenidos:", informes);
      return informes;
      
    } else {
      const nit = CONFIG.codigoCliente();
      const snapshot = await get(ref(db, `/informe/${nit}`));
      const datos = snapshot.val();
      
      await crearLog(`/informe/${nit}`, "leer");
      
      log("✅ Informe obtenido:", datos);
      return datos ? { [nit]: datos } : {};
    }
  } catch (error) {
    console.error("❌ Error en obtenerInformes:", error.message);
    return {};
  }
};

export const obtenerNoticias = () => consultarMultiple(["/coordi_ph/{nit}/noticias/"]);

export const obtenerUltimasNoticias = async () => {
  try {
    const nit = CONFIG.codigoCliente();
    if (!nit) return [];

    const snapDiccionario = await get(ref(db, `noticias/diccionario/${nit}`));
    await crearLog(`noticias/diccionario/${nit}`, "leer");
    if (!snapDiccionario.exists()) return [];

    const diccionario = snapDiccionario.val() || {};
    const ids = [diccionario.noticia_0, diccionario.noticia_1]
      .filter((id) => typeof id === "string" && id);

    const noticias = await Promise.all(
      ids.map(async (id) => {
        const snap = await get(ref(db, `coordi_ph/${nit}/noticias/${id}`));
        return snap.exists() ? { id, ...snap.val() } : null;
      })
    );

    return noticias.filter(Boolean);
  } catch (error) {
    console.error("❌ Error al obtener las últimas noticias:", error.message);
    return [];
  }
};

export const obtenerDatosConjunto = async () => {
  try {
    const nit = CONFIG.codigoCliente();
    if (!nit) return {};

    const idConjunto = await consultarSimple(`clientes/${nit}`);
    if (!idConjunto || typeof idConjunto !== "string") return {};

    const datos = await consultarSimple(`coordi_ph_clientes/${nit}/${idConjunto}`);
    return datos || {};
  } catch (error) {
    console.error("❌ Error al obtener datos del conjunto:", error.message);
    return {};
  }
};

export const obtenerVisitantes = async (meses = mesesCompletos()) => {
  const rutas = CONFIG.tipoPase.flatMap(tipo =>
    tipo === "PERMANENTE" ? ["PERMANENTE"] :
    meses.map(({ year, mes }) => `${tipo}/${year}/${mes}`)
  );
  
  return consultarConRuta(rutas.map(ruta => `/coordi_ph/{nit}/visitantes/${ruta}`));
};


export const obtenerEncomiendas = async (meses = mesesCompletos()) => {
  const paths = meses.map(({ year, mes }) => `/coordi_ph/{nit}/domicilios/${year}/${mes}`);
  // El id es la clave de Firebase: los registros no lo guardan adentro y sin él no se pueden gestionar.
  return consultarConRuta(paths);
};

export const obtenerCenso = async () => {
  const data = await consultarMultiple(["/coordi_ph/{nit}/censo"]);
  const datosConId = Object.entries(data).map(([id, objeto]) => ({
    ...objeto,
    id: id
  }));
  
  return datosConId;
};

export const obtenerReservas = async () => {
  const resultados = await Promise.all(
    CONFIG.tiposReservas.map(tipo => 
      consultarMultiple([`/coordi_ph_clientes/{nit}/reservas/${tipo}`])
    )
  );
  
  return resultados.flatMap(Object.values);
};

export const obtenerZonasActivas = async () => {
  const nit = localStorage.getItem("CodigoCliente");
  const data = await consultarSimple(`coordi_ph_clientes/${nit}`);
  if (!data) return [];

  const idConfig = Object.values(data).find(
    v => typeof v === "object" && v.usuario
  );

  if (!idConfig) return [];

  return Object.entries(idConfig)
    .filter(([_, value]) => value === true)
    .map(([key]) => key);
};

// Configuración de las zonas activas del conjunto, con el mismo catálogo e ids de la app
// (functions/zonas_catalogo.js). Cada elemento trae zonaId ("zonaGym"), zona ("gym") y sus reglas.
export const obtenerZonas = async () => {
  try {
    const nit = localStorage.getItem("CodigoCliente");
    const conjunto = await obtenerDatosConjunto();
    const activas = zonasActivasDelConjunto(conjunto);
    const resultados = await Promise.all(activas.map(z => consultarSimple(`coordi_ph_clientes/${nit}/${z.id}`)));
    return resultados.map((dataZona, i) => ({ zonaId: activas[i].id, zona: activas[i].campo, ...dataZona }));
  } catch (error) {
    console.error("❌ Error obteniendo zonas:", error);
    return [];
  }
};

export const obtenerPQRS = () =>
  consultarMultiple([`/coordi_ph/{nit}/pqrs`]);

export const obtenerPQRS_Respuesta = (id) =>
  consultarMultiple([`/coordi_ph/{nit}/pqrs/${id}/respuesta`]);


export const obtenerAnuncios = () => consultarMultiple(["/coordi_ph/{nit}/anuncios/"]);

export const obtenerNotificaciones = () => consultarMultiple(["/coordi_ph_clientes/{nit}/fcm_list"]);

export const obtenerNovedades = async (meses = mesesCompletos()) => {
  return consultarConRuta(meses.map(({ year, mes }) => `/coordi_ph/{nit}/novedad/${year}/${mes}`));
};

export const obtenerRondas = async (meses = mesesCompletos()) => {
  const rondasAntiguas = CONFIG.esSuper() 
    ? Object.values(await consultarSimple("/rondas/listado/"))
    : [];

  const rutas = meses.map(({ year, mes }) => `/rondas/{nit}/${year}/${mes}`);
  
  const rondasNuevas = Object.values(await consultarMultiple(rutas)).flat();
  
  return [...rondasAntiguas, ...rondasNuevas];
};

export const obtenerGPS = async () => {
  const data = await consultarSimple("/rastreo/");
  log("Datos brutos rastreo:", data);

  const permiso     = localStorage.getItem("Permisos");
  const correo      = (localStorage.getItem("CorreoElectronico") || "").toLowerCase();
  const razonSocial = CONFIG.razonSocial();
  const esCoordinar = correo.endsWith("@coordinarseguridad.com");
  const esAuditor   = permiso === "AUDITOR";

  if (!data) return [];

  if (esAuditor) {
    const codigoCliente = CONFIG.codigoCliente();
    const correos = (await consultarSimple("/correos")) || {};

    const registros = await Promise.all(
      Object.entries(data).map(async ([correoKey, userData]) => {
        const registro = userData.cuenta || userData[Object.keys(userData)[0]];
        if (!registro) return null;

        if (registro.cliente === razonSocial) return { id: correoKey, ...registro };

        const idUsuario = correos[correoKey];
        if (!idUsuario) return null;

        const usuario = await consultarSimple(`/usuarios/${correoKey}/${idUsuario}`);
        if (!usuario || usuario.CodigoCliente !== codigoCliente) return null;

        return {
          id: correoKey,
          ...registro,
          cliente: registro.cliente || usuario.RazonSocial || razonSocial,
        };
      })
    );

    const resultado = registros.filter(r => r && r.cliente);
    log("Resultado filtrado (auditor):", resultado);
    return resultado;
  }

  const resultado = [];
  Object.entries(data).forEach(([userId, userData]) => {
    const id = Object.keys(userData)[0];
    const userRecord = { id, ...userData[id] };

    if (userRecord.cliente && (esCoordinar || permiso === "SUPER")) {
      resultado.push(userRecord);
    }
  });

  log("Resultado filtrado:", resultado);
  return resultado;
};


export const obtenerEventos = () => consultarMultiple(["/coordi_ph/{nit}/eventos/"]);

export const obtenerCartera = () => consultarMultiple(["/coordi_ph/{nit}/cartera/"]);

const RUTAS_TICKETS = ["tickets/2023", "tickets/2024", "tickets/2025", "tickets/listado"];

export const obtenerTickets = async () => {
  const resultados = await Promise.all(RUTAS_TICKETS.map((ruta) => consultarSimple(ruta)));
  const combinados = {};
  resultados.forEach((datos, i) => {
    Object.entries(datos || {}).forEach(([id, item]) => {
      combinados[id] = { ...item, _rutaOrigen: RUTAS_TICKETS[i] };
    });
  });
  return combinados;
};

export const obtenerUsuarios = async () => {
  const esSuper = CONFIG.esSuper();
  log("🟡 obtenerUsuarios → esSuper:", esSuper);

    try {
    if (esSuper) {
      log("🟡 Consultando /correos...");
      const correosData = await consultarSimple("/correos");
      log("🟢 Respuesta /correos:", correosData);

      if (!correosData || typeof correosData !== "object") {
        console.warn("⚠️ /correos inválido");
        return {};
      }

      const entradas = Object.entries(correosData).filter(([correo, id]) => correo && id);
      const invalidas = Object.keys(correosData).length - entradas.length;
      if (invalidas) console.warn(`⚠️ ${invalidas} entradas inválidas en /correos`);

      const resultados = await enLotes(entradas, async ([correo, id]) => {
        const ruta = `/usuarios/${correo}/${id}`;

        try {
          const data = await consultarSimple(ruta);
          return data ? { correo, id, ...data } : null;
        } catch (err) {
          console.error(`❌ Error consultando ${ruta}:`, err.message);
          return null;
        }
      });

      log("🟢 Resultados finales (super):", resultados);

      return Object.fromEntries(
        resultados
          .filter(Boolean)
          .map(item => [`${item.correo}_${item.id}`, item])
      );
    }

    const nit = CONFIG.codigoCliente();

    log("🟡 Consultando autorizados y pendientes:", nit);
    const [autorizadosData, pendientesData] = await Promise.all([
      consultarSimple(`/autorizados/${nit}`),
      consultarSimple(`/pendientes/${nit}`),
    ]);
    log("🟢 Respuesta autorizados:", autorizadosData);
    log("🟢 Respuesta pendientes:", pendientesData);

    const resumen = { invalidas: 0, vacios: 0, duplicadas: 0 };

    const extraerRutas = (rutasObj) => {
      if (!rutasObj || typeof rutasObj !== "object") return [];

      const rutas = [];
      Object.values(rutasObj).forEach((item) => {
        const ruta = item?.ruta;
        if (typeof ruta === "string" && ruta) rutas.push(ruta);
        else resumen.invalidas += 1;
      });
      return rutas;
    };

    const rutasCrudas = [
      ...extraerRutas(autorizadosData),
      ...extraerRutas(pendientesData),
    ];
    const rutas = [...new Set(rutasCrudas)];
    resumen.duplicadas = rutasCrudas.length - rutas.length;

    const leidos = await enLotes(rutas, async (ruta) => {
      try {
        const data = await consultarSimple(ruta);

        if (!data || Object.keys(data).length === 0) {
          resumen.vacios += 1;
          return null;
        }

        const partes = ruta.split("/");
        const offset = ruta.startsWith("/") ? 1 : 0;
        const correo = partes[1 + offset];
        const id = partes[2 + offset];

        if (!correo || !id) {
          resumen.invalidas += 1;
          return null;
        }

        return { correo, id, ...data };
      } catch (err) {
        console.error(`❌ Error consultando ${ruta}:`, err.message);
        return null;
      }
    });

    const todos = leidos.filter(Boolean);

    if (resumen.invalidas || resumen.vacios || resumen.duplicadas) {
      console.warn(
        `⚠️ Usuarios de ${nit}: ${rutas.length} rutas leidas; ` +
        `${resumen.duplicadas} repetidas omitidas, ` +
        `${resumen.invalidas} sin .ruta (registros viejos anidados bajo el correo), ` +
        `${resumen.vacios} apuntan a un usuario que ya no existe.`
      );
    }

    const mapa = new Map();
    todos.forEach(item => mapa.set(`${item.correo}_${item.id}`, item));

    log("🟢 Resultados finales (no super):", [...mapa.values()]);

    return Object.fromEntries(mapa);
  } catch (error) {
    console.error("❌ Error general al obtener usuarios:", error);
    return {};
  }
};

export const obtenerLogs = async () => {
  try {
    const clientes = await consultarSimple("/clientes");
    if (!clientes) return [];

    const hoy = new Date();
    const año = hoy.getFullYear();
    const mes = String(hoy.getMonth() + 1).padStart(2, "0");
    const dia = String(hoy.getDate()).padStart(2, "0");

    const resultados = [];

    const consultas = Object.keys(clientes).map(async (codigoCliente) => {
      const ruta = `/logs/${codigoCliente}/${año}/${mes}/${dia}`;
      const data = await consultarSimple(ruta);

      if (!data) return;

      for (const [usuario, logs] of Object.entries(data)) {
        if (!logs) continue;

        for (const [idLog, detalles] of Object.entries(logs)) {
          if (!detalles) continue;

          if (typeof detalles.url !== "string" || !detalles.url.trim()) {
            console.warn(
              `Log sin URL omitido -> Cliente: ${codigoCliente}, Usuario: ${usuario}, ID: ${idLog}`
            );
            continue;
          }

          resultados.push({
            codigoCliente,
            año,
            mes,
            dia,
            usuario,
            idLog,
            accion: detalles.accion || "",
            permisos: detalles.permisos || "",
            dispositivo: detalles.dispositivo || "",
            version: detalles.version || "",
            fecha: detalles.fecha || "",
            url: detalles.url.trim(),
          });
        }
      }
    });

    await Promise.all(consultas);

    return resultados;
  } catch (error) {
    console.error("Error al obtener logs:", error.message);
    return [];
  }
};

export const obtenerCalificaciones = async () => {
  try {
    const data = await consultarSimple("/calificacion");
    return data || {};
  } catch (error) {
    console.error("Error al obtener calificaciones:", error.message);
    return {};
  }
};



export const obtenerClientes = async () => {
  const correo = (localStorage.getItem("CorreoElectronico") || "").toLowerCase();
  const esSuper = CONFIG.esSuper() || correo.endsWith("@coordinarseguridad.com");
  const codigoCliente = CONFIG.codigoCliente();

  try {
    if (esSuper) {
      const clientesData = await consultarSimple("/clientes");
      const consultas = Object.entries(clientesData).map(async ([codigo, id]) => {
        const data = await consultarSimple(`/coordi_ph_clientes/${codigo}/${id}`);
        return data ? { codigo, id, ...data } : null;
      });
      
      const resultados = await Promise.all(consultas);
      return Object.fromEntries(
        resultados.filter(Boolean).map(item => [`${item.codigo}_${item.id}`, item])
      );
    } else {
      const clienteId = await consultarSimple(`/clientes/${codigoCliente}`);
      if (!clienteId) return {};
      
      const clienteData = await consultarSimple(`/coordi_ph_clientes/${codigoCliente}/${clienteId}`);
      if (!clienteData) return {};
      
      return {
        [`${codigoCliente}_${clienteId}`]: {
          codigo: codigoCliente,
          id: clienteId,
          ...clienteData
        }
      };
    }
  } catch (error) {
    console.error("Error al obtener clientes:", error.message);
    return {};
  }
};

export const obtenerTutoriales = () => consultarSimple("/tutoriales/");

export const obtenerInmueble = () => consultarSimple(`/usuarios/${CONFIG.correoLimpio()}/inmueble/`);